// Minimal, safe Markdown for model-written text (assistant replies): paragraphs, line breaks, headings, bullet and
// numbered lists, block quotes, fenced code, inline code, bold, italic and http(s)/mailto links. It builds React
// elements only — never innerHTML — so text from a model or a user can never inject markup or script.
import { Fragment, type ReactNode } from "react";

type Block =
  | { kind: "p"; lines: string[] }
  | { kind: "h"; level: 1 | 2 | 3; text: string }
  | { kind: "ul" | "ol"; items: string[]; start: number }
  | { kind: "quote"; lines: string[] }
  | { kind: "code"; lang: string; text: string };

const BULLET = /^\s*[-*+]\s+(.*)$/;
const NUMBERED = /^\s*(\d{1,9})[.)]\s+(.*)$/;
const HEADING = /^(#{1,6})\s+(.*?)\s*#*\s*$/;
const FENCE = /^\s*(```|~~~)\s*([\w+-]*)\s*$/;
const QUOTE = /^\s*>\s?(.*)$/;

/** Splits text into blocks (CommonMark-like subset; unknown syntax stays plain text). */
export function parseBlocks(source: string): Block[] {
  const lines = source.replace(/\r\n?/g, "\n").split("\n");
  const out: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    const fence = FENCE.exec(line);
    if (fence) {
      const body: string[] = [];
      i++;
      while (i < lines.length && !new RegExp(`^\\s*${fence[1]}\\s*$`).test(lines[i]!)) body.push(lines[i++]!);
      i++; // closing fence (or end of text)
      out.push({ kind: "code", lang: fence[2] ?? "", text: body.join("\n") });
      continue;
    }
    if (line.trim() === "") {
      i++;
      continue;
    }
    const heading = HEADING.exec(line);
    if (heading) {
      out.push({ kind: "h", level: Math.min(3, heading[1]!.length) as 1 | 2 | 3, text: heading[2]! });
      i++;
      continue;
    }
    if (BULLET.test(line) || NUMBERED.test(line)) {
      const ordered = !BULLET.test(line);
      const re = ordered ? NUMBERED : BULLET;
      const items: string[] = [];
      const start = ordered ? Number(NUMBERED.exec(line)![1]) : 1;
      while (i < lines.length && re.test(lines[i]!)) {
        const m = re.exec(lines[i]!)!;
        let item = ordered ? m[2]! : m[1]!;
        i++;
        // Indented continuation lines belong to the item.
        while (i < lines.length && /^\s{2,}\S/.test(lines[i]!) && !BULLET.test(lines[i]!) && !NUMBERED.test(lines[i]!)) item += `\n${lines[i++]!.trim()}`;
        items.push(item);
      }
      out.push({ kind: ordered ? "ol" : "ul", items, start });
      continue;
    }
    if (QUOTE.test(line)) {
      const body: string[] = [];
      while (i < lines.length && QUOTE.test(lines[i]!)) body.push(QUOTE.exec(lines[i++]!)![1]!);
      out.push({ kind: "quote", lines: body });
      continue;
    }
    const para: string[] = [];
    while (i < lines.length && lines[i]!.trim() !== "" && !FENCE.test(lines[i]!) && !HEADING.test(lines[i]!) && !BULLET.test(lines[i]!) && !NUMBERED.test(lines[i]!) && !QUOTE.test(lines[i]!)) {
      para.push(lines[i++]!);
    }
    out.push({ kind: "p", lines: para });
  }
  return out;
}

/** A link target the panel may open: http(s) or mailto only (no javascript:, data:, relative tricks). */
export function safeHref(raw: string): string | null {
  try {
    const url = new URL(raw.trim());
    return url.protocol === "http:" || url.protocol === "https:" || url.protocol === "mailto:" ? url.href : null;
  } catch {
    return null;
  }
}

// Inline tokens, earliest match wins: code, link, bold, italic.
const INLINE = /`([^`\n]+)`|\[([^\]\n]+)\]\(([^)\s]+)\)|\*\*([^*\n]+?)\*\*|__([^_\n]+?)__|\*([^*\n]+?)\*|(?<![\w])_([^_\n]+?)_(?![\w])/;

/** Inline Markdown of one line of text to React nodes. */
export function renderInline(text: string, keyPrefix = "i"): ReactNode[] {
  const out: ReactNode[] = [];
  let rest = text;
  let n = 0;
  while (rest.length > 0) {
    const m = INLINE.exec(rest);
    if (!m) {
      out.push(rest);
      break;
    }
    if (m.index > 0) out.push(rest.slice(0, m.index));
    const key = `${keyPrefix}-${n++}`;
    if (m[1] !== undefined) out.push(<code key={key}>{m[1]}</code>);
    else if (m[2] !== undefined) {
      const href = safeHref(m[3]!);
      out.push(
        href ? (
          <a key={key} href={href} target="_blank" rel="noopener noreferrer">
            {renderInline(m[2], key)}
          </a>
        ) : (
          <Fragment key={key}>{m[0]}</Fragment>
        ),
      );
    } else if (m[4] !== undefined || m[5] !== undefined) out.push(<strong key={key}>{renderInline((m[4] ?? m[5])!, key)}</strong>);
    else out.push(<em key={key}>{renderInline((m[6] ?? m[7])!, key)}</em>);
    rest = rest.slice(m.index + m[0].length);
  }
  return out;
}

function lines(texts: readonly string[], key: string): ReactNode[] {
  return texts.flatMap((l, i) => (i === 0 ? renderInline(l, `${key}-${i}`) : [<br key={`${key}-br${i}`} />, ...renderInline(l, `${key}-${i}`)]));
}

export function Markdown({ text, className }: { text: string; className?: string }) {
  const blocks = parseBlocks(text);
  return (
    <div className={`markdown${className ? ` ${className}` : ""}`}>
      {blocks.map((b, i) => {
        const key = `b${i}`;
        switch (b.kind) {
          case "p":
            return <p key={key}>{lines(b.lines, key)}</p>;
          case "h": {
            const Tag = (["h3", "h4", "h5"] as const)[b.level - 1]!;
            return <Tag key={key}>{renderInline(b.text, key)}</Tag>;
          }
          case "ul":
            return (
              <ul key={key}>
                {b.items.map((it, j) => (
                  <li key={j}>{lines(it.split("\n"), `${key}-${j}`)}</li>
                ))}
              </ul>
            );
          case "ol":
            return (
              <ol key={key} start={b.start}>
                {b.items.map((it, j) => (
                  <li key={j}>{lines(it.split("\n"), `${key}-${j}`)}</li>
                ))}
              </ol>
            );
          case "quote":
            return <blockquote key={key}>{lines(b.lines, key)}</blockquote>;
          case "code":
            return (
              <pre key={key} data-lang={b.lang || undefined}>
                <code>{b.text}</code>
              </pre>
            );
        }
      })}
    </div>
  );
}
